'use client';

import { useBoolean } from 'minimal-shared/hooks';
import { useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import { _faqsEnSupport } from 'src/sections/support/data/faq-en-data';
import { _faqsArSupport } from 'src/sections/support/data/faq-ar-data';
import { CONFIG } from 'src/global-config';
import { Iconify } from 'src/components/iconify';
import { SupportNav } from './support-nav';
import { SupportContent } from './support-content';
import { useLocale } from 'src/locales';
import { usePathname } from 'next/navigation';
import { LinearProgress } from '@mui/material';

const iconPath = (name) => `${CONFIG.assetsDir}/assets/icons/support/${name}`;

export function SupportMain() {
  const pathname = usePathname();
  const isArabic = pathname?.startsWith('/ar');
  const language = isArabic ? 'ar' : 'en';
  const { t } = useLocale();
  const openNavMobile = useBoolean();

  // Initialize empty until translations are ready
  const [topic, setTopic] = useState('');
  const [loading, setLoading] = useState(true);

  const faqsSupport = language === 'en' ? _faqsEnSupport : _faqsArSupport;

  const TOPICS = [
    {
      title: t('Pricing_And_Billing'),
      icon: 'lucide:saudi-riyal',
      content: <SupportContent contents={faqsSupport.pricingAndBilling} />,
    },
    {
      title: t('Transport_Services'),
      icon: 'material-symbols:delivery-truck-speed-outline',
      content: <SupportContent contents={faqsSupport.transportServices} />,
    },
    {
      title: t('Business_And_Contracting'),
      icon: 'streamline-ultimate:business-contract-give',
      content: <SupportContent contents={faqsSupport.businessAndContracts} />,
    },
    {
      title: t('Shipment_Management_And_Tracking'),
      icon: 'flowbite:tracking-outline',
      content: <SupportContent contents={faqsSupport.shipmentAndTracking} />,
    },
    {
      title: t('Accounts_And_Access'),
      icon: 'ic:outline-manage-accounts',
      content: <SupportContent contents={faqsSupport.accountAndAccess} />,
    },
    {
      title: t('Platform_And_Integration'),
      icon: 'stash:integrations-duotone',
      content: <SupportContent contents={faqsSupport.platformAndIntegration} />,
    },
    {
      title: t('Reporting_And_Insights'),
      icon: 'oui:app-reporting',
      content: <SupportContent contents={faqsSupport.reportingAndInsights} />,
    },
    {
      title: t('Policies_And_Regulations'),
      icon: 'mdi:contract-outline',
      content: <SupportContent contents={faqsSupport.policiesAndRegulations} />,
    },
    {
      title: t('Company_Information'),
      icon: 'mdi:information-variant-circle-outline',
      content: <SupportContent contents={faqsSupport.companyInfo} />,
    },
  ];

  // Wait until t() returns valid translated titles before setting the default
  useEffect(() => {
    const translatedDefault = t('Pricing_And_Billing');
    if (translatedDefault && translatedDefault !== 'Pricing_And_Billing') {
      setTopic(translatedDefault);
      setLoading(false);
    }
  }, [t, language]);

  const handleChangeTopic = useCallback((event, newValue) => {
    setTopic(newValue);
  }, []);

  useEffect(() => {
    if (openNavMobile.value) {
      openNavMobile.onFalse();
    }
  }, [topic]);

  if (loading || !topic) {
    return <LinearProgress sx={{ color: 'primary.main' }} />;
  }

  return (
    <Box component="section" sx={{ bgcolor: 'background.whitePaper', py: { xs: 0, md: 5 } }}>
      <Box
        sx={(theme) => ({
          px: 2,
          py: 1.5,
          display: { md: 'none' },
          borderBottom: `solid 1px ${theme.vars.palette.divider}`,
          bgcolor: 'background.whitePaper',
        })}
      >
        <IconButton onClick={openNavMobile.onTrue}>
          <Iconify icon="carbon:menu" />
        </IconButton>
      </Box>

      <Container sx={{ pb: { xs: 5, md: 5 } }}>
        <Typography variant="h3" sx={{ my: { xs: 3, md: 10 } }}>
          {t('Frequently_asked_questions')}
        </Typography>

        <Box sx={{ gap: 10, display: 'flex' }}>
          <SupportNav
            data={TOPICS}
            topic={topic}
            open={openNavMobile.value}
            onChangeTopic={handleChangeTopic}
            onClose={openNavMobile.onFalse}
          />

          {TOPICS.map((item) => item.title === topic && <div key={item.title}>{item.content}</div>)}
        </Box>
      </Container>
    </Box>
  );
}
